import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import '@koridor/design-system/styles.css';
import './native-shell.css';
import { initNativeApp } from './lib/init-native-app';
import { ensureAudioUnlockListener } from './lib/game-feedback';
import { initNativePushListenersEarly } from './lib/native-push';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FriendsProvider } from './contexts/FriendsContext';
import { GameProvider } from './contexts/GameContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { ToastProvider } from './contexts/ToastContext';
import { AppShell } from './AppShell';
import { LoadingOverlay } from './components/LoadingOverlay';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SeoHead } from './components/SeoHead';
import { initPwaUpdates } from './lib/pwa';
import { AppleCallbackScreen } from './screens/AppleCallbackScreen';

const AuthScreen = lazy(() => import('./screens/AuthScreen').then((m) => ({ default: m.AuthScreen })));
const MenuScreen = lazy(() => import('./screens/MenuScreen').then((m) => ({ default: m.MenuScreen })));
const GameRoute = lazy(() => import('./screens/GameRoute').then((m) => ({ default: m.GameRoute })));
const ResultRoute = lazy(() => import('./screens/ResultRoute').then((m) => ({ default: m.ResultRoute })));
const ProfileScreen = lazy(() => import('./screens/ProfileScreen').then((m) => ({ default: m.ProfileScreen })));
const FriendsScreen = lazy(() => import('./screens/FriendsScreen').then((m) => ({ default: m.FriendsScreen })));
const RankScreen = lazy(() => import('./screens/RankScreen').then((m) => ({ default: m.RankScreen })));
const SettingsScreen = lazy(() => import('./screens/SettingsScreen').then((m) => ({ default: m.SettingsScreen })));
const TutorialScreen = lazy(() => import('./screens/TutorialScreen').then((m) => ({ default: m.TutorialScreen })));
const MatchmakingScreen = lazy(() => import('./screens/MatchmakingScreen').then((m) => ({ default: m.MatchmakingScreen })));
const LobbyScreen = lazy(() => import('./screens/LobbyScreen').then((m) => ({ default: m.LobbyScreen })));
const FriendProfileScreen = lazy(() => import('./screens/FriendProfileScreen').then((m) => ({ default: m.FriendProfileScreen })));
const LegalScreen = lazy(() => import('./screens/LegalScreen').then((m) => ({ default: m.LegalScreen })));
const JoinDeepLinkScreen = lazy(() => import('./screens/JoinDeepLinkScreen').then((m) => ({ default: m.JoinDeepLinkScreen })));
const InviteDeepLinkScreen = lazy(() => import('./screens/InviteDeepLinkScreen').then((m) => ({ default: m.InviteDeepLinkScreen })));

function PageFallback() {
  return (
    <div style={{ position: 'relative', flex: 1, minHeight: 200 }}>
      <LoadingOverlay />
    </div>
  );
}

function shouldPreserveAuthRedirect(pathname: string, search: string): boolean {
  if (pathname.startsWith('/invite/')) return true;
  if (pathname.startsWith('/join/')) return true;
  if (pathname === '/result' && /(?:^|[?&])rematchFrom=/.test(search)) return true;
  if (pathname === '/game' && /(?:^|[?&])matchId=/.test(search)) return true;
  return false;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageFallback />;
  if (!user) {
    if (window.__PRERENDER__) {
      return (
        <Suspense fallback={<PageFallback />}>
          <AuthScreen />
        </Suspense>
      );
    }
    if (shouldPreserveAuthRedirect(location.pathname, location.search)) {
      const redirect = `${location.pathname}${location.search}`;
      return <Navigate to={`/auth?redirect=${encodeURIComponent(redirect)}`} replace />;
    }
    return <Navigate to="/auth" replace />;
  }
  return <>{children}</>;
}

function LazyRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/auth" element={<AuthScreen />} />
        <Route path="/" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
          <Route index element={<MenuScreen />} />
          <Route path="game" element={<GameRoute />} />
          <Route path="result" element={<ResultRoute />} />
          <Route path="profile" element={<ProfileScreen />} />
          <Route path="friends" element={<FriendsScreen />} />
          <Route path="friend/:id" element={<FriendProfileScreen />} />
          <Route path="rank" element={<RankScreen />} />
          <Route path="settings" element={<SettingsScreen />} />
          <Route path="tutorial" element={<TutorialScreen />} />
          <Route path="matchmaking" element={<MatchmakingScreen />} />
          <Route path="lobby" element={<LobbyScreen />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/auth/apple/callback" element={<AppleCallbackScreen />} />
      <Route path="/legal/:page" element={<Suspense fallback={<PageFallback />}><LegalScreen /></Suspense>} />
      <Route path="/join/:code" element={<Suspense fallback={<PageFallback />}><JoinDeepLinkScreen /></Suspense>} />
      <Route path="/invite/:id" element={<Suspense fallback={<PageFallback />}><InviteDeepLinkScreen /></Suspense>} />
      <Route path="*" element={<LazyRoutes />} />
    </Routes>
  );
}

initNativeApp();
ensureAudioUnlockListener();
void initNativePushListenersEarly();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <AuthProvider>
      <FriendsProvider>
        <SettingsProvider>
          <ToastProvider>
            <GameProvider>
              <HelmetProvider>
                <BrowserRouter>
                  <SeoHead />
                  <div className="app-viewport">
                    <AppRoutes />
                  </div>
                </BrowserRouter>
              </HelmetProvider>
            </GameProvider>
          </ToastProvider>
        </SettingsProvider>
      </FriendsProvider>
    </AuthProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);

initPwaUpdates(() => {
  window.dispatchEvent(new CustomEvent('koridor-pwa-update'));
});
